import { useEffect, useRef, useState } from 'react';
import { BackButton } from '../../../components/buttons/Buttons';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { useTypedModal, type BaseResponse, type GenericResponse } from '@/common/common_utils';
import { useForm } from 'react-hook-form';
import { Column } from '@/components/container/Container';
import FileUpload4Form from '../../../components/form-inputs/FileUpload4Form';
import ResizeTextArea from '@/components/form-inputs/ResizeTextArea';
import { TextHeadlineExtraLarge, TextLabelLarge2 } from '@/components/text/Texts';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { cn } from '@/components/ui/cn';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import DropdownInput from '@/components/inputs/DropdownInput';
import { InputController } from '@/components/inputs/InputController';

const lostItemTypes = [
	{ label: 'Gadgets', value: 'Gadgets' },
	{ label: 'Wallet', value: 'Wallet' },
	{ label: 'Personal Items', value: 'Personal Items' },
	{ label: 'Others', value: 'Others' },
];

type ReportLostItemForm = {
	itemType: string;
	description: string;
	FileUrl?: string;
}

const ReportLostItemDialog = () => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploadedFileUrl, setUploadedFileUrl] = useState<string | undefined>();
	const [isDragOver, setIsDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const formName = 'reportLostItemForm';
	const form = useForm<ReportLostItemForm>({
		// defaultValues: {
		// 	ItemType: undefined,
		// 	Description: '',
		// 	FileId: 0
		// },
		resolver: yupResolver(yup.object({
			itemType: yup.string().trim().required(VALIDATION_MESSAGES.required),
			description: yup.string().trim().required(VALIDATION_MESSAGES.required)
				.min(10, "Description must be at least 10 characters long"),
		})),
		shouldFocusError: false,
		mode: 'onChange',
	});

	const { handleSubmit, formState: { errors } } = form;

	type UploadFileResponse = { FileId?: string; FileName?: string; fileName?: string; Url?: string; url?: string };

	const startUpload = async (file: File | null) => {
		setSelectedFile(file);
		setUploadedFileUrl(undefined);
		setUploadError(null);
		setUploadProgress(0);
		if (!file) return;

		// Abort any in-flight upload
		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;

		const fd = new FormData();
		fd.append('file', file, file.name);
		setUploading(true);
		try {
			const res = await axiosInstance.post<GenericResponse<UploadFileResponse>>(
				ENDPOINTS['/api/X4bConcierge/UploadLostItemFile'],
				fd,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					signal: controller.signal,
					onUploadProgress: (evt) => {
						if (!evt.total) return;
						const percent = Math.round((evt.loaded * 100) / evt.total);
						setUploadProgress(percent);
					},
				}
			);
			const data = res.data?.Data ?? (res.data as any)?.data?.Data;
			const fileUrl = data?.Url;
			setUploadedFileUrl(fileUrl);
		} catch (err: any) {
			if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
				// no-op on cancel
			} else {
				setUploadError('Upload failed');
			}
		} finally {
			setUploading(false);
			abortControllerRef.current = null;
		}
	};

	const [visible, setVisible] = useState(true);
	const modal = useTypedModal();

	const handleClose = () => {
		setVisible(false);
		setTimeout(() => modal.resolveRemove(), 300);
	};

	// The effect to sync the uploadedFileId with the form field
	useEffect(() => {
		if (uploadedFileUrl) {
			form.setValue('FileUrl', uploadedFileUrl, { shouldValidate: true });
		} else {
			form.setValue('FileUrl', undefined, { shouldValidate: true });
		}
	}, [uploadedFileUrl, form]);

	const onSubmit = async (data: ReportLostItemForm) => {
		await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bConcierge/ReportLostItem'], {
			description: data.description,
			itemType: data.itemType,
			fileId: uploadedFileUrl
		});

		handleClose();
	};

	const isValid = form.formState.isValid;

	return (
		<Sidebar
			visible={visible}
			position="right"
			onHide={handleClose}
			dismissable
			showCloseIcon
			pt={{ root: { className: 'w-[432px] mr-6 px-6 py-8 bg-white rounded-[28px] h-[calc(100vh-48px)]' } }}
			content={() => (
				<>
					<BackButton
						withIcon
						id="report-lost-item-back"
						label="Back"
						className="self-start mb-3"
						onClick={handleClose}
					/>

					<TextHeadlineExtraLarge text="Report Lost Item" className="text-neutral17 mb-5" />

					<Column className="gap-8">
						<TextLabelLarge2 text="About the Item" className="text-primary" />

						<InputController id="itemType" control={form.control}>
							<DropdownInput
								variant="outlined"
								label="Lost Item"
								options={lostItemTypes}
							/>
						</InputController>

						<ResizeTextArea
							showCharCount
							maxLength={15000}
							id="description"
							label="Item Description"
							formName={formName}
							control={form.control}
						/>
						<FileUpload4Form
							control={form.control}
							uploadEndpoint={ENDPOINTS['/api/X4bConcierge/UploadLostItemFile']}
							accept="image/*"
							title='Upload photo'
						/>
					</Column>

					<Button
						id="report-lost-item-submit"
						label="Submit"
						onClick={handleSubmit(onSubmit)}
						pt={{
							root: {
								className: cn(
									'h-12 rounded-[100px] self-end mt-auto',
									isValid && !uploading ? 'bg-primary' : 'bg-neutral80 pointer-events-none'
								)
							},
							label: {
								className: cn(
									'font-poppins text-label-large2',
									isValid && !uploading ? 'text-white' : 'text-[#949494]'
								)
							},
						}}
					/>
				</>
			)}
		/>
	);
};

export default ReportLostItemDialog;
